import React from "react";
import { useMediaQuery } from "@mui/material";

import OverviewPlotPaperProps from "./OverviewPlotPaperProps";
import OverviewPlotPaper from "./OverviewPlotPaper";
import CallToActionBox from "../UtilityComponents/PaperUtilityComponents/CallToActionBox";
import PageOption from "../../PageTypes/PageOption";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";

import { useTheme } from "@mui/material/styles";
import ShadowPaper from "../UtilityComponents/PaperUtilityComponents/ShadowPaper";
import { Collapse } from "@mui/material";
import ErrorPage from "../../ErrorPageComponents/ErrorPage";
import { isMobile } from "react-device-detect";

import OverviewPanelProps from "./OverviewPanelProps";

import OverviewIntro from "./OverviewIntro";
import OverviewConfigDisplay from "./OverviewConfigDisplay";
import OverviewSecondaryStatsProps from "./OverviewSecondaryStatsProps";

import { useContext } from "react";
import PersonalizationContext from "../../PersonalizationComponents/PersonalizationContext";

const padding = 2;

const paperPadding = isMobile ? 1.5 : 2;

const percentHeightForMainTile = "75%"; // TODO: replace this with a flex grow against smaller boxes
const percentHeightForDetailPanel = "25%";

function OverviewComplexPanel(props: OverviewPanelProps) {
  const theme = useTheme();
  const minFixedWidthForPlot = theme.spacing(75);
  const fixedSizeForSideSquares = isMobile ? "100%" : theme.spacing(50);

  const noPlansAdded = props.noStockPlansAdded && props.noESPPPlansAdded;
  const mediaQuery = useMediaQuery(theme.breakpoints.down("lg"));

  const { personalizationConfig, setPersonalizationConfig } = useContext(PersonalizationContext);
  const isCoinbaseSandbox = personalizationConfig.branding === "coinbase";

  return (
    <Stack
      sx={{
        display: "flex",
        height: "100%",
        width: "100%",
        flexDirection: { xs: "column", lg: "row" },
        gap: 2,
      }}
    >
      <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          flexGrow: 1,
          justifyContent: "flex-start",
          gap: 2,
        }}
      >
        <ShadowPaper
          PaperProps={{
            sx: {
              height: percentHeightForMainTile,
              minWidth: isMobile ? "100%" : minFixedWidthForPlot,
              flexGrow: 1,
              sizing: "border-box",
            },
          }}
          locked={noPlansAdded && props.firstStockPlansCheckDone}
          padding={paperPadding}
          unlockButtonText="Add a Stock Plan or ESPP Plan to unlock the Overview Plot"
          handleUnlock={() => {
            props.immediateStockPlanAddPrompt();
            props.setPage("stock-plans");
          }}
        >
          {!props.plotError || noPlansAdded ? (
            <OverviewPlotPaper {...props.plotProps} />
          ) : (
            <ErrorPage refreshInsteadOfBack />
          )}
          {/* TODO: bottom padding for the buttons doesn't work supremely well */}
        </ShadowPaper>
        <Paper sx={{ padding: paperPadding, flexGrow: 0 }}>
          {noPlansAdded ? (
            <OverviewIntro loading={!props.firstStockPlansCheckDone} />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <OverviewConfigDisplay
                setPage={props.setPage}
                selectedStrategyName={props.selectedStrategyName}
                selectedScenarioName={props.selectedScenarioName}
                secondaryStats={props.secondaryStatProps}
                withholdingOn={props.withholdingOn}
                withholdingRate={props.withholdingRate}
                firstWithholdingCheckDone={props.firstWithholdingCheckDone}
              />
            </Box>
          )}
        </Paper>
      </Stack>
      <Stack
        sx={{
          display: "flex",
          flexDirection: { xs: "row", lg: "column" },
          justifyContent: "flex-start",
          gap: 0,
          flexShrink: 0,
          overflow: "auto",
        }}
      >
        {[
          {
            inCondition: props.firstStockPlansCheckDone && noPlansAdded,
            boxProps: {
              title: "Have stock plans?",
              description:
                "Enter some basic information about your stock plans to use the powerful modeling tools in this app.",
              buttonText: "Configure your Stock Plans",
              urgent: true,
              onClick: () => {
                props.immediateStockPlanAddPrompt();
                props.setPage("stock-plans");
              },
            },
          },
          {
            inCondition: props.firstStockPlansCheckDone && noPlansAdded,
            boxProps: {
              title: "Have ESPP plans?",
              description:
                "Enter some basic information about your ESPPs to use the powerful modeling tools in this app.",
              buttonText: "Configure your ESPPs",
              urgent: true,
              onClick: () => {
                props.immediateESPPAddPrompt();
                props.setPage("espps");
              },
            },
          },
          {
            inConditon:
              props.firstStockPlansCheckDone &&
              !noPlansAdded &&
              props.firstStrategiesCheckDone &&
              props.numStrategiesConfigured === 0,
            boxProps: {
              title: "Not sure where to start?",
              description:
                "Don't want to think about your equity every quarter? We'll do the work for you. Compare and model our professionally-designed equity management strategies in the Strategies page. Select a strategy once and we'll automatically notify you about the trades that align with your goals!",
              buttonText: "Configure your Strategy",
              onClick: () => props.setPage("strategies"),
            },
          },
          {
            inConditon: props.firstStockPlansCheckDone && !noPlansAdded && props.numStrategiesConfigured === 1,
            boxProps: {
              title: "Compare Different Strategies?",
              description:
                "Congratulations on configuring your first strategy! Want to see how your holdings would have evolved under different strategies? Head over to the Strategies page to add another strategy and compare their history.",
              buttonText: "Add Another Strategy",
              onClick: () => props.setPage("strategies"),
            },
          },
          {
            inConditon:
              props.firstStockPlansCheckDone &&
              !noPlansAdded &&
              props.numStrategiesConfigured > 1 &&
              !props.selectedStrategyName,
            boxProps: {
              title: "Pick a Strategy",
              description:
                "Now that you've taken a look at a couple options. Lock in your favorite for the future, and we'll automatically notify you about the trades that align with your goals!",
              buttonText: "Pick Your Strategy",
              onClick: () => props.setPage("strategies"),
            },
          },
          {
            inConditon:
              props.firstStockPlansCheckDone &&
              !noPlansAdded &&
              props.firstScenariosCheckDone &&
              props.numScenariosConfigured === 0,
            boxProps: {
              title: "Have some views?",
              description:
                "Wondering what's next for your equity? Have some thoughts on your company's future? Check out our forecasts page to model your value under various future paths.",
              buttonText: "Project Some Forecasts",
              onClick: () => props.setPage("forecasts"),
            },
          },
          {
            inConditon:
              props.firstStockPlansCheckDone &&
              !noPlansAdded &&
              props.numScenariosConfigured > 1 &&
              !props.selectedScenarioName,
            boxProps: {
              title: "Pick a Forecast",
              description:
                "Now that you've taken a look at a couple options. Lock in your favorite future projection, and we'll update this overview graph to show you how your stock plan could evolve in that future.",
              buttonText: "Pick Your Forecast",
              onClick: () => props.setPage("forecasts"),
            },
          },
          {
            inConditon:
              props.firstStockPlansCheckDone &&
              !noPlansAdded &&
              props.selectedScenarioName !== undefined &&
              props.selectedStrategyName !== undefined,
            boxProps: {
              title: "All set up!",
              description:
                "Nice work! You've configured your stock plans, strategy, and forecast. You're all set up to see how your equity could evolve in the future. Keep an eye on this overview graph to see how your stock plan could evolve in the future. Keep an eye on your email to get updates on your strategy's trades! More features coming soon!",
              disabled: true,
              buttonText: "(More Coming Soon)",
              onClick: () => null,
            },
          },
          {
            inConditon:
              !(
                props.firstStockPlansCheckDone &&
                !props.noStockPlansAdded &&
                props.selectedScenarioName !== undefined &&
                props.selectedStrategyName !== undefined
              ) && !isCoinbaseSandbox,
            boxProps: {
              title: "Prefer tables?",
              description:
                "See all your equity details in one place. Check out the My Stock Plans page to see your equity details, including your vesting schedule, grant details, and more.",
              buttonText: "Explore My Stock Plans",
              onClick: () => props.setPage("stock-plans"),
            },
          },
          {
            inConditon: isCoinbaseSandbox,
            boxProps: {
              title: "Prefer tables?",
              description:
                "See all your ESPP details in one place. Check out the My ESPPs page to see your ESPP details, including your offering periods, contribution limits, and historical outcomes.",
              buttonText: "Explore My ESPPs",
              onClick: () => props.setPage("espps"),
            },
          },
        ].map(
          (
            box // TODO: we can choose to show only the first 3 of these or something...factor this out into a function?
          ) => (
            <Collapse
              key={box.boxProps.title}
              in={box.inConditon}
              orientation={mediaQuery ? "horizontal" : "vertical"}
              sx={{
                flexShrink: 0,
                maxWidth: "95%",
              }}
            >
              <Paper
                sx={{
                  width: fixedSizeForSideSquares,
                  flexShrink: 0,
                  padding: paperPadding,
                  marginBottom: { xs: 0, lg: 2 },
                  marginRight: { xs: 2, lg: 0 },
                  boxSizing: "border-box",
                }}
              >
                <CallToActionBox {...box.boxProps} />
              </Paper>
            </Collapse>
          )
        )}
      </Stack>
    </Stack>
  );
}

export default OverviewComplexPanel;
